import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import {
  addDays,
  addMonthsToDate,
  addYearsToDate,
  endOfWeek,
  formatDayLabel,
  formatMonthLabel,
  formatWeekRangeLabel,
  startOfDayLocal,
  startOfWeek,
} from "./calendarGrid";
import DayView from "./DayView";
import MonthView from "./MonthView";
import WeekView from "./WeekView";
import YearView from "./YearView";
import EventosAgrupados from "./EventosAgrupados";
import { consultaDeFeriado } from "./feriados";
import {
  CalendarHeader,
  CalendarShell,
  HeaderControls,
  HeaderTitle,
  MotionView,
  NavButton,
  NavGroup,
  PopoverCard,
  PopoverClose,
  PopoverHeader,
  PopoverList,
  PopoverOverlay,
  PopoverTitle,
  ScaleButton,
  ScaleGroup,
} from "./styles";

export const SCALES = ["day", "week", "month", "year"];

// O calendário nasceu no Planejamento de Entregas; quem o reusa para outra
// coisa (ex.: Gestão de Solicitações) troca só as palavras.
export const VOCAB_PADRAO = {
  item: ["entrega", "entregas"],
  subitem: ["ambiente", "ambientes"],
  subitemTitulo: "Ambientes",
  grupoTitulo: "Clientes",
  previsao: ["previsão sem lote", "previsões sem lote"],
};

const SCALE_LABELS = {
  day: "Dia",
  week: "Semana",
  month: "Mês",
  year: "Ano",
};

/**
 * Calendar — calendario com 4 escalas (Dia / Semana / Mes / Ano)
 * inspirado no macOS Calendar.
 *
 * Props:
 *  - events: Array<{ id, date: Date, title, subtitle?, tone, payload, previsao? }>
 *      previsao: true desenha o evento com contorno tracejado (data prevista
 *      de algo ainda nao programado — ex.: ambiente sem lote).
 *  - cursor: Date         // data ancora ("agora exibido")
 *  - scale: "day"|"week"|"month"|"year"
 *  - onCursorChange(Date)
 *  - onScaleChange(scale)
 *  - onSelectEvent(event)
 *  - emptyMessage?: string
 *  - weekStartsOn?: 0 | 1  (default 0 — domingo)
 */
export default function Calendar({
  events,
  cursor,
  scale,
  onCursorChange,
  onScaleChange,
  onSelectEvent,
  emptyMessage = "Nenhuma entrega no período.",
  weekStartsOn = 0,
  // Quando a página controla a escala por tabs próprias (ex.: Planejamento
  // de Entregas — Lista/Dia/Semana/Mês/Ano), esconde o seletor interno para
  // não duplicar o controle. A navegação (‹ Hoje ›) continua no calendário.
  hideScaleSwitcher = false,
  vocab = VOCAB_PADRAO,
  // {"AAAA-MM-DD": nome}. Ausente = feriados nacionais do navegador.
  feriados = null,
}) {
  const feriadoDe = useMemo(() => consultaDeFeriado(feriados), [feriados]);
  const today = useMemo(() => startOfDayLocal(new Date()), []);
  const [popoverDay, setPopoverDay] = useState(null);

  const title = useMemo(() => {
    if (scale === "day") return formatDayLabel(cursor);
    if (scale === "week") {
      const start = startOfWeek(cursor, weekStartsOn);
      const end = endOfWeek(cursor, weekStartsOn);
      return formatWeekRangeLabel(start, end);
    }
    if (scale === "year") return String(cursor.getFullYear());
    return formatMonthLabel(cursor.getFullYear(), cursor.getMonth());
  }, [cursor, scale, weekStartsOn]);

  const goPrev = useCallback(() => {
    if (scale === "day") onCursorChange(addDays(cursor, -1));
    else if (scale === "week") onCursorChange(addDays(cursor, -7));
    else if (scale === "year") onCursorChange(addYearsToDate(cursor, -1));
    else onCursorChange(addMonthsToDate(cursor, -1));
  }, [cursor, scale, onCursorChange]);

  const goNext = useCallback(() => {
    if (scale === "day") onCursorChange(addDays(cursor, 1));
    else if (scale === "week") onCursorChange(addDays(cursor, 7));
    else if (scale === "year") onCursorChange(addYearsToDate(cursor, 1));
    else onCursorChange(addMonthsToDate(cursor, 1));
  }, [cursor, scale, onCursorChange]);

  const goToday = useCallback(() => {
    onCursorChange(startOfDayLocal(new Date()));
  }, [onCursorChange]);

  const todayDisabled = useMemo(() => {
    if (scale === "day") return cursor.toDateString() === today.toDateString();
    if (scale === "week") {
      return startOfWeek(cursor, weekStartsOn).toDateString() ===
        startOfWeek(today, weekStartsOn).toDateString();
    }
    if (scale === "year") return cursor.getFullYear() === today.getFullYear();
    return (
      cursor.getFullYear() === today.getFullYear() &&
      cursor.getMonth() === today.getMonth()
    );
  }, [cursor, today, scale, weekStartsOn]);

  useEffect(() => {
    function onKey(event) {
      if (popoverDay) {
        if (event.key === "Escape") setPopoverDay(null);
        return;
      }
      const tag = event.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        goPrev();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        goNext();
      } else if (event.key === "t" || event.key === "T") {
        event.preventDefault();
        goToday();
      } else if (event.key === "d" || event.key === "D") {
        event.preventDefault();
        onScaleChange?.("day");
      } else if (event.key === "w" || event.key === "W") {
        event.preventDefault();
        onScaleChange?.("week");
      } else if (event.key === "m" || event.key === "M") {
        event.preventDefault();
        onScaleChange?.("month");
      } else if (event.key === "y" || event.key === "Y") {
        event.preventDefault();
        onScaleChange?.("year");
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goPrev, goNext, goToday, onScaleChange, popoverDay]);

  function handleOpenDay(date, eventsForDay) {
    if (!eventsForDay || eventsForDay.length === 0) return;
    setPopoverDay({ date, events: eventsForDay });
  }

  function handleJumpToDay(date) {
    onCursorChange(startOfDayLocal(date));
    onScaleChange?.("day");
  }

  function handleJumpToMonth(date) {
    onCursorChange(startOfDayLocal(date));
    onScaleChange?.("month");
  }

  return (
    <CalendarShell>
      <CalendarHeader>
        <HeaderTitle>{title}</HeaderTitle>
        <HeaderControls>
          {!hideScaleSwitcher && (
            <ScaleGroup role="group" aria-label="Escala do calendário">
              {SCALES.map((value) => (
                <ScaleButton
                  type="button"
                  key={value}
                  $active={scale === value}
                  aria-pressed={scale === value}
                  title={`${SCALE_LABELS[value]} (${value.charAt(0).toUpperCase()})`}
                  onClick={() => onScaleChange?.(value)}
                >
                  {SCALE_LABELS[value]}
                </ScaleButton>
              ))}
            </ScaleGroup>
          )}
          <NavGroup role="group" aria-label="Navegação" data-export-no-print>
            <NavButton
              type="button"
              onClick={goPrev}
              aria-label="Anterior"
              title="Anterior (←)"
            >
              <FiChevronLeft size={16} />
            </NavButton>
            <NavButton
              type="button"
              onClick={goToday}
              disabled={todayDisabled}
              title="Hoje (T)"
            >
              Hoje
            </NavButton>
            <NavButton
              type="button"
              onClick={goNext}
              aria-label="Próximo"
              title="Próximo (→)"
            >
              <FiChevronRight size={16} />
            </NavButton>
          </NavGroup>
        </HeaderControls>
      </CalendarHeader>

      <AnimatePresence mode="wait" initial={false}>
        <MotionView
          key={`${scale}-${cursor.getFullYear()}-${cursor.getMonth()}-${cursor.getDate()}`}
          initial={{ opacity: 0, x: 6 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -6 }}
          transition={{ duration: 0.16 }}
        >
          {scale === "day" && (
            <DayView
              feriadoDe={feriadoDe}
              cursor={cursor}
              events={events}
              onSelectEvent={onSelectEvent}
              emptyMessage={emptyMessage}
              vocab={vocab}
            />
          )}
          {scale === "week" && (
            <WeekView
              feriadoDe={feriadoDe}
              cursor={cursor}
              events={events}
              weekStartsOn={weekStartsOn}
              onSelectEvent={onSelectEvent}
              onJumpToDay={handleJumpToDay}
              emptyMessage={emptyMessage}
            />
          )}
          {scale === "month" && (
            <MonthView
              feriadoDe={feriadoDe}
              cursor={cursor}
              events={events}
              weekStartsOn={weekStartsOn}
              onSelectEvent={onSelectEvent}
              onOpenDay={handleOpenDay}
              emptyMessage={emptyMessage}
            />
          )}
          {scale === "year" && (
            <YearView
              feriadoDe={feriadoDe}
              cursor={cursor}
              events={events}
              weekStartsOn={weekStartsOn}
              onJumpToMonth={handleJumpToMonth}
              onJumpToDay={handleJumpToDay}
              vocab={vocab}
            />
          )}
        </MotionView>
      </AnimatePresence>

      <AnimatePresence>
        {popoverDay && (
          <PopoverOverlay onMouseDown={() => setPopoverDay(null)}>
            <PopoverCard
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.14 }}
              onMouseDown={(domEvent) => domEvent.stopPropagation()}
            >
              <PopoverHeader>
                <PopoverTitle>{formatDayLabel(popoverDay.date)}</PopoverTitle>
                <PopoverClose
                  type="button"
                  onClick={() => setPopoverDay(null)}
                  aria-label="Fechar"
                >
                  ×
                </PopoverClose>
              </PopoverHeader>
              <PopoverList>
                <EventosAgrupados
                  eventos={popoverDay.events}
                  onSelectEvent={(ev) => {
                    setPopoverDay(null);
                    onSelectEvent?.(ev);
                  }}
                />
              </PopoverList>
            </PopoverCard>
          </PopoverOverlay>
        )}
      </AnimatePresence>
    </CalendarShell>
  );
}
